import {
  MajorCareerMatrixResponse,
  MajorsBrowseResponse,
  ItemsResponse,
  RelevanceResponse,
  RelevanceMode,
  ExploreFiltersResponse,
  MajorsGuideOverviewResponse,
} from '@backrs_ui/grow-ui/src/pages/MajorsGuide/types';

import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithAuth } from '../baseQuery';

export const majorsGuideApi = createApi({
  reducerPath: 'majorsGuideApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: [
    'MajorCareerMatrix',
    'MajorsBrowse',
    'GuideItems',
    'Relevance',
    'ExploreFilters',
    'MajorsGuideOverview',
  ],
  endpoints: (builder) => ({
    getMajorCareerMatrix: builder.query<MajorCareerMatrixResponse, undefined>({
      query: () => 'major-career-matrix',
      // Cache for 24 hours (matrix data rarely changes)
      keepUnusedDataFor: 60 * 60 * 24,
      providesTags: ['MajorCareerMatrix'],
    }),

    // Browse majors with filtering/sorting/pagination
    getMajorsBrowse: builder.query<
      MajorsBrowseResponse,
      {
        colleges?: string[];
        sort?: string;
        order?: 'asc' | 'desc';
        search?: string;
        page?: number;
        perPage?: number;
      }
    >({
      query: ({ colleges, ...params }) => ({
        url: 'majors/guide/browse',
        params: {
          ...params,
          // Send colleges as comma-separated string for backend compatibility
          college: colleges?.join(',') || undefined,
        },
      }),
      providesTags: ['MajorsBrowse'],
    }),

    // Get selectable items (paths or majors)
    getGuideItems: builder.query<ItemsResponse, { type: 'paths' | 'majors' }>({
      query: ({ type }) => `majors/guide/items?type=${type}`,
      keepUnusedDataFor: 60 * 60 * 24, // Cache 24h
      providesTags: ['GuideItems'],
    }),

    // Get relevance rankings
    getRelevance: builder.query<
      RelevanceResponse,
      {
        mode: RelevanceMode;
        selections: string;
        primary?: string;
        levels?: string;
        page?: number;
        perPage?: number;
      }
    >({
      query: (params) => ({
        url: 'majors/guide/relevance',
        params,
      }),
      providesTags: ['Relevance'],
    }),

    // Get explore page filter options (college types + sort values)
    getExploreFilters: builder.query<ExploreFiltersResponse, undefined>({
      query: () => 'majors/guide/explore/filters',
      // Cache for 24 hours (filter options rarely change)
      keepUnusedDataFor: 60 * 60 * 24,
      providesTags: ['ExploreFilters'],
    }),

    // Every major reduced to id/name/college, plus the distinct college types
    getMajorsGuideOverview: builder.query<MajorsGuideOverviewResponse, undefined>({
      query: () => 'majors/guide/overview',
      // Cache for 24 hours (overview data rarely changes)
      keepUnusedDataFor: 60 * 60 * 24,
      providesTags: ['MajorsGuideOverview'],
    }),
  }),
});

export const {
  useGetMajorCareerMatrixQuery,
  useGetMajorsBrowseQuery,
  useGetGuideItemsQuery,
  useGetRelevanceQuery,
  useGetExploreFiltersQuery,
  useGetMajorsGuideOverviewQuery,
} = majorsGuideApi;
